import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';

/**
 * Builds an AI context string around a source SMS message.
 *
 * Messages are normalized to chronological order (oldest -> newest) using a
 * non-mutating reverse copy so the original array is never changed.
 *
 * Output format:
 * `<before>older messages</before> <target>source message</target> <after>newer messages</after>`
 *
 * @param sourceId - ID of the message that should be treated as the target.
 * @param messages - Messages in thread order (commonly newest -> oldest).
 * @returns Formatted context string, or an empty string when input is invalid or source is not found.
 */
export const generateContext = (sourceId: string, messages?: SMS[]): string => {
  if (!messages?.length) return '';

  const chronologicalMessages = [...messages].reverse();

  const currentIndex = chronologicalMessages.findIndex((msg) => msg.id === sourceId);
  if (currentIndex === -1) return '';

  const currentMessage = chronologicalMessages[currentIndex].body;
  if (!currentMessage?.trim()) return '';

  const previousMessages = chronologicalMessages
    .slice(0, currentIndex)
    .map((msg) => msg.body)
    .filter((body) => body?.trim())
    .join(', ');
  const nextMessages = chronologicalMessages
    .slice(currentIndex + 1)
    .map((msg) => msg.body)
    .filter((body) => body?.trim())
    .join(', ');

  return `<before>${previousMessages}</before> <target>${currentMessage}</target> <after>${nextMessages}</after>`;
};
